import { FC, useContext, useState } from 'react';

import { Button, H2, InfoBlock, Modal, NumberInput } from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';
import { NavLink } from 'react-router-dom';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { ITelegramModalProps } from './interfaces';
import s from './TelegramModal.module.scss';

export const TelegramModal: FC<ITelegramModalProps> = ({
  onClose,
  updateGoods,
  loadedGoods,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { telegramChannelId, telegramNotificationTypes },
    },
  } = useContext(SettingsContext);

  const [error, setError] = useState<boolean>(false);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<{ threshold: number | '' }>({
    mode: 'onChange',
    defaultValues: { threshold: '' },
  });

  return (
    <Modal onClose={onClose}>
      <form
        onSubmit={handleSubmit(({ threshold }) => {
          return updateGoods({
            ids: loadedGoods?.map((good) => good.id),
            input: {
              low_stock_notification: {
                enabled: true,
                threshold: threshold === '' ? undefined : threshold,
              },
            },
          }).then(onClose);
        })}
      >
        <div className={s.container}>
          <H2 className={s.title}>{t('telegramModal.title')}</H2>
          <InfoBlock
            color="blue"
            iconName="InfoIcon"
            message={
              !telegramChannelId?.id ? (
                <span>
                  {t('telegramModal.info.in_setting')}{' '}
                  <NavLink
                    state={{ selectedTab: 'INTEGRATIONS' }}
                    to={'/settings'}
                    className={s.link}
                  >
                    {t('integration')}
                  </NavLink>{' '}
                </span>
              ) : +telegramNotificationTypes.low_stock ? (
                <span>
                  {t('telegramModal.info.text_2')}{' '}
                  <NavLink
                    state={{ selectedTab: 'INTEGRATIONS' }}
                    to={'/settings'}
                    className={s.link}
                  >
                    {t('integration')}
                  </NavLink>{' '}
                </span>
              ) : (
                <span>
                  {t('telegramModal.info.text_3')}{' '}
                  <NavLink
                    state={{ selectedTab: 'INTEGRATIONS' }}
                    to={'/settings'}
                    className={s.link}
                  >
                    {t('integration')}
                  </NavLink>{' '}
                </span>
              )
            }
            className={s.info}
          />
          <div className={s.input}>
            <Controller
              render={({ field, fieldState: { error } }) => (
                <NumberInput
                  {...field}
                  disabled={!telegramChannelId?.id}
                  title={t('telegramModal.input.placeholder')}
                  rightContent={`${t('pcs')}`}
                  onChange={(value) => {
                    field.onChange(value);
                    if (value >= 999 || value < 0) {
                      setError(true);
                    } else setError(false);

                    if (value === '') {
                      setError(false);
                    }
                  }}
                  placeholder="5"
                  error={error?.message}
                />
              )}
              name={'threshold'}
              rules={{
                min: {
                  value: 1,
                  message: t('errors.min', 1, t('pcs')),
                },
                max: {
                  value: 999,
                  message: t('errors.max', '999', t('pcs')),
                },
              }}
              control={control}
            />
            <p className={s.text}>{t('telegramModal.text')}</p>
          </div>
          <Button
            disabled={!telegramChannelId?.id || error}
            isLoading={isSubmitting}
            type="submit"
            fullWidth
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
